import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ScanLine, ShieldCheck, ShieldAlert, Calendar, User } from "lucide-react";

export default function FsmLookup() {
  const [serial, setSerial] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function lookup(e) {
    e?.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/warranties/lookup/${encodeURIComponent(serial)}`);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }

  return (
    <div className="px-5 py-6 space-y-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">Field Action</div>
      <h2 className="font-display text-3xl font-bold tracking-tighter">Warranty lookup.</h2>

      <form onSubmit={lookup} className="space-y-3">
        <div className="relative">
          <ScanLine className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input value={serial} onChange={(e) => setSerial(e.target.value)} required
            placeholder="Enter serial code"
            data-testid="lookup-serial-input"
            className="w-full bg-[#0A1128] border border-[#1E293B] focus:border-[#FF5C00] outline-none pl-10 pr-3 py-4 font-mono text-base text-white" />
        </div>
        <button type="submit" disabled={loading} data-testid="lookup-submit"
          className="btn-tactile w-full bg-[#FF5C00] text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#D4FF00] hover:text-[#0A1128] transition-colors">
          {loading ? "Searching…" : "Lookup"}
        </button>
      </form>

      {result && (
        <div className="border border-[#1E293B] bg-[#0A1128] p-5 mt-6" data-testid="lookup-result">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{result.warranty.serial_code}</div>
              <div className="font-display text-xl font-semibold mt-1">{result.material?.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">{result.material?.code}</div>
            </div>
            {result.is_active ? (
              <div className="flex items-center gap-1 text-[#D4FF00]">
                <ShieldCheck className="w-5 h-5" /> <span className="font-mono text-xs uppercase tracking-widest">In Warranty</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-red-400">
                <ShieldAlert className="w-5 h-5" /> <span className="font-mono text-xs uppercase tracking-widest">Out of Warranty</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4 text-xs font-mono">
            <div>
              <div className="text-[#94A3B8]">Commissioned</div>
              <div className="mt-0.5">{new Date(result.warranty.commissioning_date).toLocaleDateString()}</div>
            </div>
            <div>
              <div className="text-[#94A3B8]">Expires</div>
              <div className="mt-0.5">{new Date(result.warranty.expiry_date).toLocaleDateString()}</div>
            </div>
            <div>
              <div className="text-[#94A3B8]">Material warranty</div>
              <div className="mt-0.5">{result.warranty.warranty_days} days</div>
            </div>
            <div>
              <div className="text-[#94A3B8]">Elapsed</div>
              <div className="mt-0.5">{result.warranty.elapsed_days} days</div>
            </div>
            <div className="col-span-2 pt-3 border-t border-[#1E293B]">
              <div className="text-[#94A3B8] flex items-center gap-1"><User className="w-3 h-3" /> Customer</div>
              <div className="mt-0.5">{result.customer?.customer_name} · {result.customer?.phone}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[#94A3B8] flex items-center gap-1"><Calendar className="w-3 h-3" /> Days remaining</div>
              <div className={"mt-0.5 font-display text-3xl font-bold " + (result.is_active ? "text-[#D4FF00]" : "text-red-400")}>
                {result.is_active ? result.days_left : Math.abs(result.days_left) + " over"}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
